import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import EmptyState from '../../../components/EmptyState';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import type { FeedbackState } from '../../../types/feedback';
import { getTimeSections } from './feedbackUtils';
import { FloatingLegend } from './FloatingLegend';
import { UserFeedbackStateCard } from './UserFeedbackStateCard';

interface UserFeedbackStatesProps {
    feedbackStates: FeedbackState[];
}

export default function UserFeedbackStates({ feedbackStates }: UserFeedbackStatesProps) {
    const { formatTimeSince } = useFormatUtils();
    const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());

    if (feedbackStates.length === 0) {
        return (
            <EmptyState
                title="No data available"
                description="No user feedback states found."
            />
        );
    }

    // Calculate max actions for color scaling
    const maxActions = Math.max(...feedbackStates.map(state => {
        const actionEntries = Object.entries(state.actionCounts || {}) as [string, number][];
        return actionEntries.reduce((sum, [, count]) => sum + count, 0);
    }));

    const timeSections = getTimeSections(feedbackStates);

    const handleToggleSection = (index: number) => {
        setCollapsedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    return (
        <div className="space-y-6">
            <FloatingLegend feedbackStates={feedbackStates} timeSections={timeSections} />
            {timeSections.map((section, index) => {
                const isCollapsed = collapsedSections.has(index);
                return (
                    <div key={index} className="space-y-3">
                        <button
                            onClick={() => handleToggleSection(index)}
                            className="w-full flex items-center gap-2 text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 hover:text-gray-700 transition-colors"
                        >
                            {isCollapsed ? (
                                <ChevronRight size={20} className="flex-shrink-0" />
                            ) : (
                                <ChevronDown size={20} className="flex-shrink-0" />
                            )}
                            <span>
                                {section.title} ({section.states.length})
                            </span>
                        </button>
                        {!isCollapsed && (
                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                {section.states.map(state => (
                                    <UserFeedbackStateCard
                                        key={state.id}
                                        state={state}
                                        maxActions={maxActions}
                                        formatTimeSince={formatTimeSince}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

        </div>
    );
}
